
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { Button } from "@/components/ui/button"
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"

const assessmentSchema = z.object({
  // Basic Information
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  companyName: z.string().min(1, "Company name is required"),
  country: z.string().min(1, "Country is required"),
  reason: z.string().min(10, "Please provide more detail about your goals"),
  
  // Business Overview
  industry: z.string().min(1, "Industry is required"),
  businessStage: z.string().min(1, "Business stage is required"),
  employeeCount: z.string().min(1, "Employee count is required"),
  annualRevenue: z.string().min(1, "Annual revenue is required"),
  businessDescription: z.string().min(20, "Please provide a more detailed business description"),
  
  // Market & Strategy
  targetMarket: z.string().min(10, "Please describe your target market in more detail"),
  competitiveAdvantage: z.string().min(10, "Please describe your competitive advantage"),
  challenges: z.string().min(10, "Please describe your main challenges"),
  
  // Goals & Operations
  goals: z.string().min(10, "Please describe your business goals in more detail"),
  revenueModel: z.string().min(10, "Please describe your revenue model"),
  marketingChannels: z.string().min(5, "Please list your marketing channels"),
  keyMetrics: z.string().min(5, "Please describe your key metrics"),
})

export type AssessmentFormData = z.infer<typeof assessmentSchema>

interface AssessmentFormProps {
  defaultValues?: AssessmentFormData
  onSubmit: (values: AssessmentFormData) => Promise<void>
  isLoading?: boolean
  readOnly?: boolean
}

export function AssessmentForm({
  defaultValues,
  onSubmit,
  isLoading = false,
  readOnly = false,
}: AssessmentFormProps) {
  const form = useForm<AssessmentFormData>({
    resolver: zodResolver(assessmentSchema),
    defaultValues: defaultValues || {
      fullName: "",
      companyName: "",
      country: "",
      reason: "",
      industry: "",
      businessStage: "",
      employeeCount: "",
      annualRevenue: "",
      businessDescription: "",
      targetMarket: "",
      competitiveAdvantage: "",
      challenges: "",
      goals: "",
      revenueModel: "",
      marketingChannels: "",
      keyMetrics: "",
    },
  })

  const handleSubmit = async (values: AssessmentFormData) => {
    if (!readOnly) {
      await onSubmit(values)
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
            <div className="w-4 h-4 bg-white rounded-full"></div>
          </div>
        </div>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Business Strategy Assessment</h1>
          <p className="text-gray-600 mt-2">Help us understand your business better to provide personalized recommendations</p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-8">
          {/* Your Information Section */}
          <Card>
            <CardHeader>
              <CardTitle>Your Information</CardTitle>
              <CardDescription>Tell us about yourself and your business</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="fullName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full Name *</FormLabel>
                      <FormControl>
                        <Input placeholder="Your full name" {...field} disabled={readOnly} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="companyName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Company Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Your company name" {...field} disabled={readOnly} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Country *</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value} disabled={readOnly}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select your country" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="us">United States</SelectItem>
                        <SelectItem value="ca">Canada</SelectItem>
                        <SelectItem value="uk">United Kingdom</SelectItem>
                        <SelectItem value="au">Australia</SelectItem>
                        <SelectItem value="de">Germany</SelectItem>
                        <SelectItem value="fr">France</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Reason for Using CSO Agent */}
          <Card>
            <CardHeader>
              <CardTitle>Reason for Using CSO Agent</CardTitle>
              <CardDescription>What are you hoping to achieve with our platform?</CardDescription>
            </CardHeader>
            <CardContent>
              <FormField
                control={form.control}
                name="reason"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Your Reason</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Describe what you're hoping to achieve with CSO Agent..."
                        className="min-h-[100px]"
                        {...field} 
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Business Overview Section */}
          <Card>
            <CardHeader>
              <CardTitle>Business Overview</CardTitle>
              <CardDescription>Help us understand your business fundamentals</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="industry"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Industry *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value} disabled={readOnly}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your industry" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="technology">Technology</SelectItem>
                          <SelectItem value="healthcare">Healthcare</SelectItem>
                          <SelectItem value="finance">Finance</SelectItem>
                          <SelectItem value="retail">Retail/E-commerce</SelectItem>
                          <SelectItem value="manufacturing">Manufacturing</SelectItem>
                          <SelectItem value="consulting">Consulting</SelectItem>
                          <SelectItem value="real-estate">Real Estate</SelectItem>
                          <SelectItem value="education">Education</SelectItem>
                          <SelectItem value="hospitality">Hospitality</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="businessStage"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Business Stage *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value} disabled={readOnly}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select business stage" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="idea">Idea Stage</SelectItem>
                          <SelectItem value="startup">Startup</SelectItem>
                          <SelectItem value="growth">Growth Stage</SelectItem>
                          <SelectItem value="established">Established</SelectItem>
                          <SelectItem value="enterprise">Enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="employeeCount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Employee Count *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value} disabled={readOnly}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select employee count" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="just-me">Just me</SelectItem>
                          <SelectItem value="2-5">2-5 employees</SelectItem>
                          <SelectItem value="6-25">6-25 employees</SelectItem>
                          <SelectItem value="26-100">26-100 employees</SelectItem>
                          <SelectItem value="100+">100+ employees</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="annualRevenue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Annual Revenue *</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value} disabled={readOnly}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select revenue range" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="pre-revenue">Pre-revenue</SelectItem>
                          <SelectItem value="0-100k">$0 - $100K</SelectItem>
                          <SelectItem value="100k-500k">$100K - $500K</SelectItem>
                          <SelectItem value="500k-1m">$500K - $1M</SelectItem>
                          <SelectItem value="1m-5m">$1M - $5M</SelectItem>
                          <SelectItem value="5m+">$5M+</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="businessDescription"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Description *</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Describe what your business does, your products/services, and your value proposition..."
                        className="min-h-[120px]"
                        {...field} 
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormDescription>
                      Provide a clear description of your business model, key offerings, and what makes you unique
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Market & Strategy Section */}
          <Card>
            <CardHeader>
              <CardTitle>Market & Strategy</CardTitle>
              <CardDescription>Tell us about your market position and competitive landscape</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="targetMarket"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Target Market *</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Describe your ideal customers, market segments, and customer demographics..."
                        className="min-h-[100px]"
                        {...field} 
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormDescription>
                      Who are your ideal customers? What markets do you serve?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="competitiveAdvantage"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Competitive Advantage *</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="What sets you apart from competitors? Your unique strengths, differentiators..."
                        className="min-h-[100px]"
                        {...field} 
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormDescription>
                      What makes you different and better than your competition?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="challenges"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Current Challenges *</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="What are your biggest business challenges, obstacles, or pain points right now..."
                        className="min-h-[100px]"
                        {...field} 
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormDescription>
                      What obstacles are preventing you from achieving your goals?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Goals & Operations Section */}
          <Card>
            <CardHeader>
              <CardTitle>Goals & Operations</CardTitle>
              <CardDescription>Help us understand your objectives and how you operate</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="goals"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Business Goals *</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="What are your main business goals for the next 6-12 months? Growth targets, milestones..."
                        className="min-h-[100px]"
                        {...field} 
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormDescription>
                      What do you want to achieve in the next 6-12 months?
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="revenueModel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Revenue Model *</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="How do you make money? Subscription, one-time sales, services, commission..."
                        className="min-h-[80px]"
                        {...field} 
                        disabled={readOnly}
                      />
                    </FormControl>
                    <FormDescription>
                      Describe how your business generates revenue
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="marketingChannels"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Marketing Channels *</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Social media, email, SEO, paid ads, referrals..."
                          className="min-h-[80px]"
                          {...field} 
                          disabled={readOnly}
                        />
                      </FormControl>
                      <FormDescription>
                        How do you currently reach customers?
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="keyMetrics"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Key Performance Metrics *</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder="Revenue, customers, conversion rates, retention..."
                          className="min-h-[80px]"
                          {...field} 
                          disabled={readOnly}
                        />
                      </FormControl>
                      <FormDescription>
                        What metrics do you track to measure success?
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          {!readOnly && (
            <div className="flex justify-center">
              <Button 
                type="submit" 
                size="lg" 
                className="w-full max-w-md bg-blue-600 hover:bg-blue-700"
                disabled={isLoading}
              >
                {isLoading ? "Saving Assessment..." : "Complete Assessment"}
              </Button>
            </div>
          )}
        </form>
      </Form>
    </div>
  )
}
